<template>
  <modal-default
    ref="modal"
    title="Duplicate Found"
    :short-circuit-close="true"
    @close="cancelAndClose"
  >
    <!-- Duplicate Leads -->
    <template v-if="duplicateLeads.length">
      <p class="duplicate_message">
        There {{ isPlural ? "are" : "is" }} already
        {{ isPlural ? "leads" : "a lead" }} with that information. Please update
        {{ isPlural ? "one of" : "the" }} lead{{ isPlural ? "s" : "" }} below
        instead of adding this new lead.
      </p>

      <hr class="duplicate_line" />

      <div
        v-for="lead in duplicateLeads"
        :key="lead.record_id"
        class="duplicate_lead"
      >
        <p class="duplicate_lead--status">{{ lead.status_label }}</p>
        <p
          class="duplicate_lead--address"
          @click="openRecordModal(lead.record_id)"
        >
          {{ lead.address }}
        </p>

        <template v-for="contact in lead.contacts">
          <div class="pl-5 mt-3">
            <p v-if="lead.buyer_id === contact.id">(Buyer)</p>
            <p v-if="lead.seller_id === contact.id">(Seller)</p>
            <p>{{ contactName(contact) }}</p>
            <p v-if="contact.email_address">{{ contact.email_address }}</p>
            <p v-if="contact.phone_1">
              {{ contact.phone_1 }} <span>(primary)</span>
            </p>
            <p v-if="contact.phone_2">{{ contact.phone_2 }}</p>
            <p v-if="contact.phone_3">{{ contact.phone_3 }}</p>
            <p v-if="contact.phone_4">{{ contact.phone_4 }}</p>
            <p v-if="contact.phone_5">{{ contact.phone_5 }}</p>
            <p v-if="contact.phone_6">{{ contact.phone_6 }}</p>
            <p v-if="contact.phone_7">{{ contact.phone_7 }}</p>
            <p v-if="contact.phone_8">{{ contact.phone_8 }}</p>
            <p v-if="contact.phone_9">{{ contact.phone_9 }}</p>
            <p v-if="contact.phone_10">{{ contact.phone_10 }}</p>

            <p v-if="contact.street">{{ contact.street }}</p>
            <p>
              <template v-if="contact.city">{{ contact.city }}, </template>
              <template v-if="contact.state">{{ contact.state }}</template>
              <template v-if="contact.postcode">{{
                contact.postcode
              }}</template>
            </p>
          </div>
        </template>
      </div>
    </template>

    <!-- Duplicate Contact -->
    <template v-if="duplicateContacts.length">
      <hr v-if="duplicateLeads.length && duplicateContacts.length" />
      <p class="duplicate_message">
        There {{ isContactPlural ? "are" : "is" }} already
        {{ isContactPlural ? "contacts" : "a contact" }} with that contact
        information. In order to accurately keep track of communications you can
        not have multiple contacts with the same contact information.
      </p>

      <p class="duplicate_message" v-if="canAttachReplacementInstead">
        Would you like to attach the contact below or cancel and make changes?
      </p>
      <hr />

      <template v-for="(contact, index) in duplicateContacts">
        <button-outlined
          class="is-pulled-right"
          v-if="canAttachReplacementInstead"
          @click="attachContact(contact)"
        >
          Create with this Contact
        </button-outlined>
        <button-outlined
          class="is-pulled-right"
          v-else
          @click="openContactModal(contact.id)"
        >
          View Contact
        </button-outlined>

        <DetailSection heading="Name">
          <p>{{ contactName(contact) }}</p>
        </DetailSection>

        <DetailSection v-if="showPhoneSection(contact)" heading="Phone">
          <p v-if="contact.phone_1">
            {{ contact.phone_1 }} <span>(primary)</span>
          </p>
          <p v-if="contact.phone_2">{{ contact.phone_2 }}</p>
          <p v-if="contact.phone_3">{{ contact.phone_3 }}</p>
          <p v-if="contact.phone_4">{{ contact.phone_4 }}</p>
          <p v-if="contact.phone_5">{{ contact.phone_5 }}</p>
          <p v-if="contact.phone_6">{{ contact.phone_6 }}</p>
          <p v-if="contact.phone_7">{{ contact.phone_7 }}</p>
          <p v-if="contact.phone_8">{{ contact.phone_8 }}</p>
          <p v-if="contact.phone_9">{{ contact.phone_9 }}</p>
          <p v-if="contact.phone_10">{{ contact.phone_10 }}</p>
        </DetailSection>

        <DetailSection v-if="contact.email_address" heading="Email">
          <p>{{ contact.email_address }}</p>
        </DetailSection>

        <DetailSection
          v-if="showContactAddressSection(contact)"
          heading="Address"
        >
          <p v-if="contact.street">{{ contact.street }}</p>
          <p>
            <template v-if="contact.city">{{ contact.city }}, </template>
            <template v-if="contact.state">{{ contact.state }}</template>
            <template v-if="contact.postcode">{{ contact.postcode }}</template>
          </p>
        </DetailSection>

        <hr v-if="index + 1 !== duplicateContacts.length" />
      </template>
    </template>

    <template v-if="usersWithAccess.length">
      <hr />
      <p class="duplicate_message">
        Users who have access to the Original Lead.
      </p>
      <hr class="duplicate_line" />

      <div
        v-for="user in usersWithAccess"
        :key="user.id"
        class="duplicate_lead"
      >
        <grid-row>
          <grid-column :size="1">
            <app-avatar
              :display-tool-tip="false"
              :user-id="user.id"
              :include-full-name="true"
              :size="28"
              class="record_icons--avatar"
            ></app-avatar>
          </grid-column>
          <grid-column :size="11">
            <div class="mt-2 ml-2">{{ userFullName(user.id) }}</div>
          </grid-column>
        </grid-row>
      </div>
    </template>

    <!-- Footer -->
    <template v-slot:footer>
      <div class="duplicate_footer">
        <button-outlined v-if="showCreateAnywayButton" @click="createAnyway"
          >Create Anyway</button-outlined
        >

        <button-default @click="cancelAndClose">Close</button-default>
      </div>
    </template>
  </modal-default>
</template>

<script>
import { defineComponent } from "vue";
import { destroyModal } from "../../services/modals";
import DetailSection from "./components/DetailSection.vue";
import Console from "../../services/console";

export default defineComponent({
  name: "DuplicateWarningModal",
  components: { DetailSection },
  props: {
    duplicateLeads: {
      type: Array,
      default: () => [],
    },
    duplicateContacts: {
      default: () => [],
    },
    usersWithAccess: {
      type: Array,
      default() {
        return [];
      },
    },
    canAttachReplacementInstead: {
      type: Boolean,
      default: true,
    },
  },
  mounted() {
    this.$refs.modal.open();
    // Console.log(
    //   "Duplicate Warning Modal Props:",
    //   this.duplicateLeads,
    //   this.duplicateContacts,
    //   this.canAttachReplacementInstead
    // );
  },
  computed: {
    showCreateAnywayButton() {
      return !!this.duplicateLeads.length && !this.duplicateContacts.length;
    },
    isPlural() {
      return this.duplicateLeads.length > 1;
    },
    isContactPlural() {
      return this.duplicateContacts.length > 1;
    },
  },
  methods: {
    userFullName(userId) {
      let user = this.$store.getters.users.find((u) => u.id === userId);
      return user.first_name + " " + user.last_name;
    },
    contactName(contact) {
      return this.$utils.displayFullName(contact.full_name);
    },
    showPhoneSection(contact) {
      return (
        contact.phone_1 ||
        contact.phone_2 ||
        contact.phone_3 ||
        contact.phone_4 ||
        contact.phone_5 ||
        contact.phone_6 ||
        contact.phone_7 ||
        contact.phone_8 ||
        contact.phone_9 ||
        contact.phone_10
      );
    },
    showContactAddressSection(contact) {
      return (
        contact.street || contact.city || contact.state || contact.postcode
      );
    },
    cancelAndClose() {
      destroyModal(this.$refs.modal);
    },
    attachContact(contact) {
      this.$emit("attachContact", contact);
      this.cancelAndClose();
    },
    createAnyway() {
      this.$emit("override");
      this.cancelAndClose();
    },
    openRecordModal(id) {
      this.$emit("openRecordModal", id);
      this.cancelAndClose();
    },
    openContactModal(contactId) {
      this.$emit("openContactModal", contactId);
      this.cancelAndClose();
    },
  },
});
</script>

<style scoped lang="scss">
@import "../../styles/variables/variables";

.duplicate_message {
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.duplicate_line {
  margin: 0.5rem 0;
}

.duplicate_lead {
  margin-top: 1rem;

  &--status {
    margin-bottom: 4px;
    font-size: 0.75rem;
  }

  &--address {
    font-size: 1.125rem;
    font-weight: bold;
    color: $link;
    cursor: pointer;

    &:hover {
      color: $link-hover;
    }
  }
}

.duplicate_footer {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
</style>
